import React, { useState, useEffect, useRef } from 'react';
import { getProjects, getProjectById, createProject, deleteProject, exportProject, importProject, renameProject, duplicateProject, importQortexProject, formatApiError } from '../../api/client';
import { ProjectSummary } from '../../api/models';
import { SceneState, useSceneStore } from '../../state/sceneStore';
import {
  Edit2,
  Copy,
  Trash2,
  FolderOpen,
  Download,
  Upload,
  Plus,
  Loader2,
} from 'lucide-react';
import { useUserStore, getAvailableSensorTypes } from '../../state/userStore';

interface ProjectBrowserModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type SortOption = 'newest' | 'oldest' | 'name_asc' | 'name_desc';

type TransferProgress = { loaded: number; total: number; percent: number };

const formatTransferBytes = (bytes: number): string => {
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const TransferProgressBar: React.FC<{ progress: TransferProgress; label: string }> = ({
  progress,
  label,
}) => (
  <div style={{ marginBottom: '16px' }} data-testid="transfer-progress">
    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8', fontSize: '13px', marginBottom: '6px' }}>
      <span>{label}</span>
      <span>
        {formatTransferBytes(progress.loaded)} / {formatTransferBytes(progress.total)} ({progress.percent}%)
      </span>
    </div>
    <div style={{ height: '8px', backgroundColor: '#334155', borderRadius: '4px', overflow: 'hidden' }}>
      <div
        style={{
          height: '100%',
          width: `${progress.percent}%`,
          backgroundColor: '#2563eb',
          transition: 'width 0.15s ease',
        }}
      />
    </div>
  </div>
);

const IMPORT_ACCEPT_WITH_ALIGNMENT = '.json,.xml,.zip,.qdesign';
const IMPORT_ACCEPT_WITHOUT_ALIGNMENT = '.json,.xml';
const IMPORT_LABEL_WITH_ALIGNMENT = 'Select Project File (.json, .xml, .zip, or .qdesign)';
const IMPORT_LABEL_WITHOUT_ALIGNMENT = 'Select Project File (.json or .xml)';

const isAlignmentArchiveFile = (fileName: string): boolean => {
  const lowerName = fileName.toLowerCase();
  return lowerName.endsWith('.zip') || lowerName.endsWith('.qdesign');
};

const ProjectBrowserModal: React.FC<ProjectBrowserModalProps> = ({ isOpen, onClose }) => {
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [newProjectName, setNewProjectName] = useState('');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<ProjectSummary | null>(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importFileName, setImportFileName] = useState('');
  const [importNewName, setImportNewName] = useState('');
  const [isQortexImport, setIsQortexImport] = useState(false);
  const [qortexSensors, setQortexSensors] = useState<string[]>([]);
  const [defaultSensorType, setDefaultSensorType] = useState('HD');
  const [sensorTypeOverrides, setSensorTypeOverrides] = useState<Record<string, string>>({});
  const [showAdvancedSensors, setShowAdvancedSensors] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [sortOption, setSortOption] = useState<SortOption>('newest');
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [projectToExport, setProjectToExport] = useState<ProjectSummary | null>(null);
  const [exportIncludeScans, setExportIncludeScans] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<TransferProgress | null>(null);
  const [importProgress, setImportProgress] = useState<TransferProgress | null>(null);
  const [isImportProcessing, setIsImportProcessing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [openingProjectId, setOpeningProjectId] = useState<number | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const updateCurrentProject = useSceneStore((state: SceneState) => state.updateCurrentProject);
  const alignmentFeatureEnabled = useUserStore((state) => state.hasFeature('alignment'));
  const advancedSensorsEnabled = useUserStore((state) => state.hasFeature('advanced_sensors'));
  const qortexSensorTypes = getAvailableSensorTypes(advancedSensorsEnabled);

  const fetchProjects = async () => {
    try {
      setFetchError(null);
      const projectsData = await getProjects();
      setProjects(projectsData);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setFetchError('Failed to load projects. Please try again.');
    }
  };

  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      fetchProjects();
    }
  }, [isOpen]);

  const getSortedProjects = () => {
    const query = searchQuery.trim().toLowerCase();
    const filtered = query
      ? projects.filter((project) => project.name.toLowerCase().includes(query))
      : [...projects];
    switch (sortOption) {
      case 'newest':
        return filtered.sort((a, b) => b.id - a.id);
      case 'oldest':
        return filtered.sort((a, b) => a.id - b.id);
      case 'name_asc':
        return filtered.sort((a, b) => a.name.localeCompare(b.name));
      case 'name_desc':
        return filtered.sort((a, b) => b.name.localeCompare(a.name));
      default:
        return filtered;
    }
  };

  const handleCreateProject = async () => {
    if (newProjectName.trim() === '') return;
    try {
      const newProject = await createProject(newProjectName);
      setNewProjectName('');
      updateCurrentProject(newProject); // Automatically open the new project
      onClose(); // Close the modal
    } catch (error: any) {
      console.error('Error creating project:', error);

      // Check if it's a name conflict error (409 status code)
      if (error.response?.status === 409) {
        const errorMessage = error.response?.data?.detail || 'Project name already exists';
        alert(`Failed to create project: ${errorMessage}`);
        return;
      }

      const errorMessage = error.response?.data?.detail || 'Failed to create project.';
      alert(errorMessage);
    }
  };

  const handleDeleteProject = async (projectId: number) => {
    try {
      await deleteProject(projectId);
      fetchProjects(); // Refresh project list
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  const handleDeleteClick = (project: ProjectSummary) => {
    setProjectToDelete(project);
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    if (projectToDelete) {
      await handleDeleteProject(projectToDelete.id);
      setShowDeleteConfirmation(false);
      setProjectToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setProjectToDelete(null);
  };

  const handleOpenProject = async (project: ProjectSummary) => {
    if (openingProjectId !== null) {
      return;
    }

    setOpeningProjectId(project.id);
    try {
      const fullProject = await getProjectById(project.id);
      updateCurrentProject(fullProject);

      if (project.id) {
        try {
          const state = useSceneStore.getState();
          if (state.checkAndLoadCachedHeatmap) {
            await state.checkAndLoadCachedHeatmap(project.id);
          }
        } catch (error) {
          console.error('Failed to check cached heatmap:', error);
        }
      }

      onClose();
    } catch (error) {
      console.error('Error opening project:', error);
      alert('Failed to open project. Please try again.');
    } finally {
      setOpeningProjectId(null);
    }
  };

  const handleExportProject = (project: ProjectSummary) => {
    setProjectToExport(project);
    setExportIncludeScans(false);
    setExportProgress(null);
    setShowExportDialog(true);
  };

  const handleConfirmExport = async () => {
    if (!projectToExport) return;

    setIsExporting(true);
    setExportProgress(null);
    try {
      const blob = await exportProject(
        projectToExport.id,
        { includeAlignmentScans: exportIncludeScans },
        (progress) => setExportProgress(progress)
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const safeName = projectToExport.name.replace(/[^a-zA-Z0-9\s-_]/g, '').trim();
      link.download = exportIncludeScans ? `${safeName}.qdesign.zip` : `${safeName}.json`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setShowExportDialog(false);
      setProjectToExport(null);
    } catch (error) {
      console.error('Error exporting project:', error);
      alert('Failed to export project. Please try again.');
    } finally {
      setIsExporting(false);
      setExportProgress(null);
    }
  };

  const handleDuplicateProject = async (project: ProjectSummary) => {
    try {
      await duplicateProject(project.id);
      fetchProjects();
    } catch (error) {
      console.error('Error duplicating project:', error);
      alert('Failed to duplicate project.');
    }
  };

  const handleStartRename = (project: ProjectSummary) => {
    setEditingProjectId(project.id);
    setEditingName(project.name);
  };

  const handleSaveRename = async () => {
    if (!editingProjectId || !editingName.trim()) {
      setEditingProjectId(null);
      return;
    }

    try {
      await renameProject(editingProjectId, editingName.trim());
      fetchProjects();
    } catch (error) {
      console.error('Error renaming project:', error);
      alert('Failed to rename project.');
    }
    setEditingProjectId(null);
  };

  const handleCancelRename = () => {
    setEditingProjectId(null);
    setEditingName('');
  };

  const handleImportClick = () => {
    setShowImportDialog(true);
    setImportFileName('');
    setImportNewName('');
    setImportProgress(null);
    setIsImportProcessing(false);
    setIsQortexImport(false);
    setQortexSensors([]);
    setDefaultSensorType('HD');
    setSensorTypeOverrides({});
    setShowAdvancedSensors(false);
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (isAlignmentArchiveFile(file.name) && !alignmentFeatureEnabled) {
        alert('Alignment scan archives (.zip, .qdesign) are not available for this account.');
        event.target.value = '';
        setImportFileName('');
        setImportNewName('');
        setIsQortexImport(false);
        setQortexSensors([]);
        return;
      }

      setImportFileName(file.name);
      // Suggest a name based on filename
      const baseName = file.name
        .replace(/\.(json|xml|zip|qdesign)$/i, '')
        .replace('_project_export', '');
      setImportNewName(baseName);

      const lowerName = file.name.toLowerCase();
      const isXml = lowerName.endsWith('.xml');
      setIsQortexImport(isXml);

      if (isXml) {
        try {
          const text = await file.text();
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(text, "text/xml");
          const lidars = Array.from(xmlDoc.querySelectorAll("Sensors > Lidar > frameId")).map(node => node.textContent || "Unknown");
          setQortexSensors(lidars);
        } catch (err) {
          console.error("Error parsing XML for sensors", err);
        }
      }
    }
  };

  const handleImportProject = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      alert('Please select a file to import.');
      return;
    }

    setIsImporting(true);
    setImportProgress(null);
    setIsImportProcessing(false);
    try {
      let importedProject;
      if (isQortexImport) {
        // Build the sensor types mapping
        const sensorTypesMapping: Record<string, string> = {};
        qortexSensors.forEach(sensorId => {
          sensorTypesMapping[sensorId] = sensorTypeOverrides[sensorId] || defaultSensorType;
        });
        importedProject = await importQortexProject(file, importNewName || undefined, JSON.stringify(sensorTypesMapping));
      } else {
        importedProject = await importProject(
          file,
          importNewName || undefined,
          (progress) => setImportProgress(progress),
          () => {
            setImportProgress(null);
            setIsImportProcessing(true);
          }
        );
      }
      
      setShowImportDialog(false);
      setImportFileName('');
      setImportNewName('');
      fetchProjects(); // Refresh project list
      // Re-fetch full project to ensure sensors/obstacles are loaded and not overwritten by later PUTs
      const hydrated = await getProjectById(importedProject.id);
      updateCurrentProject(hydrated); // Open the imported project

      // Show info message about reference image
      if (importedProject.reference_image_url) {
        alert('Project imported successfully! Reference image has been imported.');
      } else if (importedProject.reference_image_original_filename) {
        alert('Project imported successfully! Note: Reference image could not be imported. You can re-upload it if needed.');
      } else {
        alert('Project imported successfully!');
      }

      onClose(); // Close the modal
    } catch (error: any) {
      console.error('Error importing project:', error);

      // Check if it's a name conflict error (409 status code)
      if (error.response?.status === 409) {
        const errorMessage = error.response?.data?.detail || 'Project name already exists';
        alert(`Import failed: ${errorMessage}\n\nPlease change the project name above and try again.`);
        return; // Stay in the import dialog so user can change the name
      }

      const errorMessage = formatApiError(
        error,
        'Failed to import project. Please check the file format.'
      );
      alert(`Import failed: ${errorMessage}`);
    } finally {
      setIsImporting(false);
      setImportProgress(null);
      setIsImportProcessing(false);
    }
  };

  const handleCancelImport = () => {
    setShowImportDialog(false);
    setImportFileName('');
    setImportNewName('');
    setIsQortexImport(false);
    setQortexSensors([]);
    setDefaultSensorType('HD');
    setSensorTypeOverrides({});
    setShowAdvancedSensors(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!isOpen) {
    return null;
  }

  const modalStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  };

  const contentStyle: React.CSSProperties = {
    backgroundColor: 'rgba(15, 23, 42, 0.95)',
    color: 'white',
    padding: '30px',
    borderRadius: '12px',
    border: '1px solid #334155',
    minWidth: '600px',
    maxWidth: '800px',
    maxHeight: '80vh',
    overflowY: 'auto',
    position: 'relative',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '15px',
    borderBottom: '1px solid #334155',
  };

  const closeButtonStyle: React.CSSProperties = {
    background: 'transparent',
    color: '#94a3b8',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    padding: '5px',
    borderRadius: '4px',
  };

  const projectListStyle: React.CSSProperties = {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    marginBottom: '20px',
  };

  const projectItemStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    marginBottom: '8px',
    backgroundColor: 'rgba(25, 35, 45, 0.6)',
    borderRadius: '6px',
    border: '1px solid #334155',
  };

  const projectNameStyle: React.CSSProperties = {
    flex: 1,
    marginRight: '10px',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const buttonStyle: React.CSSProperties = {
    background: '#4f46e5',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '6px 12px',
    cursor: 'pointer',
    marginLeft: '8px',
    fontSize: '14px',
  };

  const secondaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    background: '#475569',
  };

  const deleteButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    background: '#ef4444',
  };

  const inputGroupStyle: React.CSSProperties = {
    display: 'flex',
    gap: '10px',
    marginTop: '20px',
    paddingTop: '20px',
    borderTop: '1px solid #334155',
  };

  const inputStyle: React.CSSProperties = {
    flex: 1,
    background: '#334155',
    color: 'white',
    border: '1px solid #475569',
    borderRadius: '4px',
    padding: '8px 12px',
    fontSize: '14px',
  };

  const sortSelectStyle: React.CSSProperties = {
    background: '#334155',
    color: 'white',
    border: '1px solid #475569',
    borderRadius: '4px',
    padding: '6px 12px',
    fontSize: '14px',
    marginLeft: '10px',
    cursor: 'pointer',
  };

  const editInputStyle: React.CSSProperties = {
    background: '#1e293b',
    color: 'white',
    border: '1px solid #4f46e5',
    borderRadius: '4px',
    padding: '4px 8px',
    fontSize: '16px',
    width: '100%',
  };

  return (
    <div style={modalStyle} onClick={onClose}>
      <div style={contentStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <h2 style={{ margin: 0, color: 'white' }}>Projects</h2>
            <select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value as SortOption)}
              style={sortSelectStyle}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="name_asc">Name (A-Z)</option>
              <option value="name_desc">Name (Z-A)</option>
            </select>
          </div>
          <button onClick={onClose} style={closeButtonStyle} title="Close">
            ×
          </button>
        </div>

        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search projects by name..."
          style={{ ...inputStyle, marginBottom: '15px' }}
        />

        {fetchError && (
          <div style={{ color: '#f87171', marginBottom: '15px', textAlign: 'center' }}>
            {fetchError}
          </div>
        )}

        <ul style={projectListStyle}>
          {getSortedProjects().map(project => (
            <li key={project.id} style={projectItemStyle}>
              <div style={projectNameStyle}>
                {editingProjectId === project.id ? (
                  <input
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveRename();
                      if (e.key === 'Escape') handleCancelRename();
                    }}
                    onBlur={handleSaveRename}
                    autoFocus
                    style={editInputStyle}
                  />
                ) : (
                  <>
                    <span onClick={() => handleOpenProject(project)} style={{ cursor: 'pointer', flex: 1 }}>
                      {project.name}
                    </span>
                    <button
                      onClick={() => handleStartRename(project)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#94a3b8',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                      title="Rename"
                    >
                      <Edit2 size={14} />
                    </button>
                  </>
                )}
              </div>
              <div style={{ display: 'flex', gap: '5px' }}>
                <button
                  onClick={() => handleOpenProject(project)}
                  disabled={openingProjectId !== null}
                  style={{
                    ...buttonStyle,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    opacity: openingProjectId !== null && openingProjectId !== project.id ? 0.5 : 1,
                  }}
                  title="Open Project"
                >
                  {openingProjectId === project.id ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <FolderOpen size={16} />
                  )}
                  {openingProjectId === project.id ? 'Opening...' : 'Open'}
                </button>
                <button onClick={() => handleDuplicateProject(project)} style={{ ...secondaryButtonStyle, display: 'flex', alignItems: 'center', gap: '0px', padding: '6px 10px' }} title="Duplicate Project">
                  <Copy size={16} />
                </button>
                <button onClick={() => handleExportProject(project)} style={{ ...secondaryButtonStyle, display: 'flex', alignItems: 'center', gap: '0px', padding: '6px 10px' }} title="Export Project">
                  <Download size={16} />
                </button>
                <button onClick={() => handleDeleteClick(project)} style={{ ...deleteButtonStyle, display: 'flex', alignItems: 'center', gap: '0px', padding: '6px 10px' }} title="Delete Project">
                  <Trash2 size={16} />
                </button>
              </div>
            </li>
          ))}
        </ul>

        {projects.length === 0 && !fetchError && (
          <div style={{ textAlign: 'center', color: '#94a3b8', marginBottom: '20px' }}>
            No projects found. Create your first project below.
          </div>
        )}

        {projects.length > 0 && getSortedProjects().length === 0 && (
          <div style={{ textAlign: 'center', color: '#94a3b8', marginBottom: '20px' }}>
            No projects match your search.
          </div>
        )}

        <div style={inputGroupStyle}>
          <input
            type="text"
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            placeholder="New project name"
            style={inputStyle}
            onKeyPress={(e) => e.key === 'Enter' && handleCreateProject()}
          />
          <button onClick={handleCreateProject} style={{ ...buttonStyle, display: 'flex', alignItems: 'center', gap: '5px' }}>
            <Plus size={16} /> Create Project
          </button>
          <button onClick={handleImportClick} style={{ ...secondaryButtonStyle, display: 'flex', alignItems: 'center', gap: '5px' }}>
            <Upload size={16} /> Import Project
          </button>
        </div>

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirmation && projectToDelete && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1003,
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              maxWidth: '400px',
              width: '90%',
            }}>
              <h3 style={{ margin: '0 0 15px 0', color: '#dc2626' }}>Delete Project</h3>
              <p style={{ margin: '0 0 20px 0', color: '#374151', lineHeight: '1.5' }}>
                Are you sure you want to delete the project "{projectToDelete.name}"?
                This action cannot be undone and will permanently delete the project and all associated files.
              </p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={handleCancelDelete}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  Delete Project
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Export Project Dialog */}
        {showExportDialog && projectToExport && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
          }}>
            <div style={{
              backgroundColor: '#1e293b',
              padding: '20px',
              borderRadius: '8px',
              border: '1px solid #334155',
              minWidth: '400px',
              maxWidth: '500px',
            }}>
              <h3 style={{ margin: '0 0 12px 0', color: 'white' }}>Export Project</h3>
              <p style={{ margin: '0 0 16px 0', color: '#94a3b8', fontSize: '14px' }}>
                Export &quot;{projectToExport.name}&quot;. Alignment poses and status are always included.
                {exportIncludeScans
                  ? ' Scan data is packaged as a compressed .qdesign.zip archive.'
                  : ' Metadata is exported as JSON.'}
              </p>
              {alignmentFeatureEnabled && (
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', color: '#e2e8f0', marginBottom: '20px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={exportIncludeScans}
                    onChange={(event) => setExportIncludeScans(event.target.checked)}
                    disabled={isExporting}
                    style={{ marginTop: '3px' }}
                  />
                  <span>
                    Include alignment scan data (large files)
                  </span>
                </label>
              )}
              {exportProgress && (
                <TransferProgressBar progress={exportProgress} label="Downloading export..." />
              )}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowExportDialog(false);
                    setProjectToExport(null);
                  }}
                  disabled={isExporting}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#475569',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isExporting ? 'not-allowed' : 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmExport}
                  disabled={isExporting}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isExporting ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  {isExporting && <Loader2 size={14} />}
                  Export
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Import Project Dialog */}
        {showImportDialog && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
          }}>
            <div style={{
              backgroundColor: '#1e293b',
              padding: '20px',
              borderRadius: '8px',
              border: '1px solid #334155',
              minWidth: '400px',
              maxWidth: '500px',
            }}>
              <h3 style={{ margin: '0 0 20px 0', color: 'white' }}>Import Project</h3>

              <div style={{ marginBottom: '15px' }}>
                <label htmlFor="import-project-file" style={{ display: 'block', color: '#e2e8f0', marginBottom: '5px' }}>
                  {alignmentFeatureEnabled ? IMPORT_LABEL_WITH_ALIGNMENT : IMPORT_LABEL_WITHOUT_ALIGNMENT}
                </label>
                <input
                  id="import-project-file"
                  ref={fileInputRef}
                  type="file"
                  accept={alignmentFeatureEnabled ? IMPORT_ACCEPT_WITH_ALIGNMENT : IMPORT_ACCEPT_WITHOUT_ALIGNMENT}
                  onChange={handleFileSelect}
                  disabled={isImporting}
                  style={{
                    width: '100%',
                    padding: '8px',
                    backgroundColor: '#334155',
                    color: 'white',
                    border: '1px solid #475569',
                    borderRadius: '4px',
                  }}
                />
                {importFileName && (
                  <div style={{ color: '#94a3b8', fontSize: '14px', marginTop: '5px' }}>
                    Selected: {importFileName}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', color: '#e2e8f0', marginBottom: '5px' }}>
                  Project Name
                </label>
                <input
                  type="text"
                  value={importNewName}
                  onChange={(e) => setImportNewName(e.target.value)}
                  placeholder="Enter project name (required if name conflicts)"
                  style={{
                    width: '100%',
                    padding: '8px',
                    backgroundColor: '#334155',
                    color: 'white',
                    border: '1px solid #475569',
                    borderRadius: '4px',
                  }}
                />
              </div>

              {isQortexImport && qortexSensors.length > 0 && (
                <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#0f172a', borderRadius: '6px', border: '1px solid #334155' }}>
                  <h4 style={{ margin: '0 0 10px 0', color: 'white', fontSize: '14px' }}>Sensor Configuration</h4>
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', color: '#e2e8f0', marginBottom: '5px', fontSize: '13px' }}>
                      Default Sensor Type
                    </label>
                    <select
                      value={defaultSensorType}
                      onChange={(e) => setDefaultSensorType(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '6px',
                        backgroundColor: '#334155',
                        color: 'white',
                        border: '1px solid #475569',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}
                    >
                      {qortexSensorTypes.map((sensorType) => (
                        <option key={sensorType} value={sensorType}>{sensorType}</option>
                      ))}
                    </select>
                  </div>
                  
                  <button 
                    onClick={() => setShowAdvancedSensors(!showAdvancedSensors)}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      color: '#3b82f6', 
                      cursor: 'pointer', 
                      padding: 0, 
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    {showAdvancedSensors ? 'Hide Advanced' : 'Show Advanced (Individual Sensors)'}
                  </button>

                  {showAdvancedSensors && (
                    <div style={{ marginTop: '10px', maxHeight: '150px', overflowY: 'auto', paddingRight: '5px' }}>
                      {qortexSensors.map(sensorId => (
                        <div key={sensorId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <span style={{ color: '#cbd5e1', fontSize: '13px' }}>Sensor {sensorId}</span>
                          <select
                            value={sensorTypeOverrides[sensorId] || defaultSensorType}
                            onChange={(e) => setSensorTypeOverrides(prev => ({ ...prev, [sensorId]: e.target.value }))}
                            style={{
                              padding: '4px',
                              backgroundColor: '#334155',
                              color: 'white',
                              border: '1px solid #475569',
                              borderRadius: '4px',
                              fontSize: '12px',
                              width: '100px'
                            }}
                          >
                            {qortexSensorTypes.map((sensorType) => (
                              <option key={sensorType} value={sensorType}>{sensorType}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {importProgress && (
                <TransferProgressBar progress={importProgress} label="Uploading project file..." />
              )}
              {isImportProcessing && (
                <div
                  style={{ marginBottom: '16px', color: '#94a3b8', fontSize: '13px' }}
                  data-testid="import-processing"
                >
                  Processing import on server (restoring scans). This may take several minutes for large projects...
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={handleCancelImport}
                  disabled={isImporting}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#475569',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleImportProject}
                  disabled={isImporting}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: isImporting ? '#94a3b8' : '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isImporting ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  {isImporting && <Loader2 size={16} className="animate-spin" />}
                  {isImporting ? 'Importing...' : 'Import Project or Q-TRACK Settings'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectBrowserModal;

